import type { GraphQLFormattedError } from "graphql";

import { PARSING_RESUME_ERROR_MESSAGE } from "./resumeUploadErrors";

/** Which step failed. "Nothing was destroyed" and "the resume is gone" need different copy. */
export const ResumeUploadErrorStage = {
  Upload: "upload",
  ReplaceDelete: "replaceDelete",
  PostDeleteCreate: "postDeleteCreate",
} as const;

export type ResumeUploadErrorStage =
  (typeof ResumeUploadErrorStage)[keyof typeof ResumeUploadErrorStage];

/** A bundle delete failed during quota recovery. The seeker still has their resume. */
export class ResumeReplaceDeleteError extends Error {
  readonly stage = ResumeUploadErrorStage.ReplaceDelete;
}

/** The retry failed after the delete committed. The seeker may now have no resume. */
export class ResumeReplacePostDeleteCreateError extends Error {
  readonly stage = ResumeUploadErrorStage.PostDeleteCreate;

  readonly pfsvErrorCode: string | undefined;

  constructor(message: string, pfsvErrorCode?: string) {
    super(message);
    this.pfsvErrorCode = pfsvErrorCode;
  }
}

// COLLECTION_LIMIT_EXCEEDED in ProfileFileServiceGraphqlErrorCode.java, capping uploaded
// resumes at one. PFSV_E-122 carried quota rejections until PFSV-754 but now means a write
// failure, so matching it here would delete a resume on a transient error.
const PROFILE_FILE_SERVICE_RESUME_QUOTA_EXCEEDED_ERROR = "PFSV_E-126";

function stringContainsResumeQuotaExceededError(value: string | undefined): boolean {
  return value?.includes(PROFILE_FILE_SERVICE_RESUME_QUOTA_EXCEEDED_ERROR) ?? false;
}

function isParsingError(error: Pick<GraphQLFormattedError, "message">): boolean {
  return error.message.includes(PARSING_RESUME_ERROR_MESSAGE);
}

/**
 * True only when quota is the sole blocker. Recovery deletes the seeker's resume, so quota
 * alongside another fatal error must not trigger it, since the retry would fail regardless.
 */
export function isRecoverableQuotaRejection(errors: readonly GraphQLFormattedError[]): boolean {
  const blockingErrors = errors.filter((gqlError) => !isParsingError(gqlError));

  return (
    blockingErrors.length > 0 &&
    blockingErrors.every((gqlError) => stringContainsResumeQuotaExceededError(gqlError.message))
  );
}

/** Uses every so a real error mixed with NOT_FOUND still surfaces. */
export function isBundleAlreadyDeletedError(errors: readonly GraphQLFormattedError[]): boolean {
  return (
    errors.length > 0 &&
    errors.every(
      (gqlError) =>
        gqlError.extensions?.code === "NOT_FOUND" || /not found/i.test(gqlError.message),
    )
  );
}

/** A parse failure is reported via the parse:failed tag, not treated as a create error. */
export function hasNonParsingCreateError(errors: readonly GraphQLFormattedError[]): boolean {
  return errors.some((gqlError) => !isParsingError(gqlError));
}

/** Send only the PFSV_E code to RUM, never raw messages or extensions which can carry PII. */
export function extractPfsvErrorCode(
  errors: readonly GraphQLFormattedError[] | undefined,
): string | undefined {
  for (const gqlError of errors ?? []) {
    const match = gqlError.message.match(/PFSV_E-\d+/);
    if (match) {
      return match[0];
    }
  }
  return undefined;
}
